import { gql } from '@apollo/client';

export const GET_CUSTOMER_QUERY = gql`
  query GetCustomer {
    customer {
      id
      companyName
      plan
      billingEmail
      emailNotifications
      webhookNotifications
      smsNotifications
    }
  }
`;

export const UPDATE_CUSTOMER_MUTATION = gql`
  mutation UpdateCustomer($input: UpdateCompanyInput!) {
    updateCompany(input: $input) {
      id
      companyName
      plan
      billingEmail
      emailNotifications
      webhookNotifications
      smsNotifications
    }
  }
`;

// Get current user with role info
export const GET_CURRENT_USER_WITH_ROLE = gql`
  query GetCurrentUserWithRole {
    me {
      id
      username
      email
      role
      isAdmin
      isEditor
      isViewer
      company {
        id
        name
      }
    }
  }
`;

// Get all users in the company (Admin only)
export const GET_COMPANY_USERS = gql`
  query GetCompanyUsers {
    companyUsers {
      id
      userId
      username
      email
      role
      isPrimary
      joinedAt
      isViewer
      isEditor
      isAdmin
    }
  }
`;

// Update a user's role
export const UPDATE_USER_ROLE = gql`
  mutation UpdateUserRole($input: UpdateUserRoleInput!) {
    updateUserRole(input: $input) {
      id
      userId
      username
      email
      role
      isViewer
      isEditor
      isAdmin
    }
  }
`;

// Remove a user from the company
export const REMOVE_USER_FROM_COMPANY = gql`
  mutation RemoveUserFromCompany($userId: Int!) {
    removeUserFromCompany(userId: $userId)
  }
`;

// Invite a user to the company
export const INVITE_USER = gql`
  mutation InviteUser($input: InviteUserInput!) {
    inviteUser(input: $input) {
      id
      email
      role
      status
      invitedByUsername
      createdAt
      expiresAt
    }
  }
`;

// Get pending invitations
export const GET_PENDING_INVITATIONS = gql`
  query GetPendingInvitations {
    pendingInvitations {
      id
      email
      role
      status
      invitedByUsername
      createdAt
      expiresAt
    }
  }
`;

// Cancel an invitation
export const CANCEL_INVITATION = gql`
  mutation CancelInvitation($invitationId: ID!) {
    cancelInvitation(invitationId: $invitationId)
  }
`;

// Resend an invitation
export const RESEND_INVITATION = gql`
  mutation ResendInvitation($invitationId: ID!) {
    resendInvitation(invitationId: $invitationId) {
      id
      expiresAt
    }
  }
`;

// Get invitation info by token (for accept page)
export const GET_INVITATION_INFO = gql`
  query GetInvitationInfo($token: String!) {
    invitationInfo(token: $token) {
      email
      companyName
      role
      invitedByUsername
      isValid
      errorMessage
    }
  }
`;

// Accept an invitation
export const ACCEPT_INVITATION = gql`
  mutation AcceptInvitation($input: AcceptInvitationInput!) {
    acceptInvitation(input: $input) {
      id
      username
      email
      role
      company {
        id
        name
      }
    }
  }
`;

// Types
export interface CompanyUser {
  id: number;
  userId: number;
  username: string;
  email: string;
  role: string;
  isPrimary: boolean;
  joinedAt: string;
  isViewer: boolean;
  isEditor: boolean;
  isAdmin: boolean;
}

export interface CurrentUserWithRole {
  id: number;
  username: string;
  email: string;
  role: string;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  company: {
    id: string;
    name: string;
  } | null;
}

export interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  invitedByUsername: string | null;
  createdAt: string;
  expiresAt: string;
}

export interface InvitationInfo {
  email: string;
  companyName: string;
  role: string;
  invitedByUsername: string | null;
  isValid: boolean;
  errorMessage: string | null;
}

// Evaluation API (key + secret for POST evaluate)
export const GET_EVALUATION_API_KEYS = gql`
  query GetEvaluationApiKeys {
    evaluationApiKeys {
      id
      name
      keyMasked
      sampleRatePer100
      isActive
      createdAt
    }
  }
`;

export const GET_EVALUATION_TYPES = gql`
  query GetEvaluationTypes($evaluationApiKeyId: ID) {
    evaluationTypes(evaluationApiKeyId: $evaluationApiKeyId) {
      id
      evaluationApiKeyId
      evaluationApiKeyName
      evaluationKey
      label
      description
      customTests
      createdAt
      updatedAt
      totalRuns
      completedRuns
      failedRuns
      pendingRuns
      lastRunAt
      customTestsPassRate
    }
  }
`;

export const CREATE_EVALUATION_TYPE = gql`
  mutation CreateEvaluationType($input: CreateEvaluationTypeInput!) {
    createEvaluationType(input: $input) {
      id
      evaluationApiKeyId
      evaluationApiKeyName
      evaluationKey
      label
      description
      customTests
      createdAt
      updatedAt
      totalRuns
      completedRuns
      failedRuns
      pendingRuns
      lastRunAt
      customTestsPassRate
    }
  }
`;

export const UPDATE_EVALUATION_TYPE = gql`
  mutation UpdateEvaluationType($input: UpdateEvaluationTypeInput!) {
    updateEvaluationType(input: $input) {
      id
      evaluationApiKeyId
      evaluationApiKeyName
      evaluationKey
      label
      description
      customTests
      createdAt
      updatedAt
      totalRuns
      completedRuns
      failedRuns
      pendingRuns
      lastRunAt
      customTestsPassRate
    }
  }
`;

export const DELETE_EVALUATION_TYPE = gql`
  mutation DeleteEvaluationType($id: ID!) {
    deleteEvaluationType(id: $id)
  }
`;

export const CREATE_EVALUATION_API_KEY = gql`
  mutation CreateEvaluationApiKey($input: CreateEvaluationAPIKeyInput!) {
    createEvaluationApiKey(input: $input) {
      id
      name
      key
      secret
      sampleRatePer100
    }
  }
`;

export const REVOKE_EVALUATION_API_KEY = gql`
  mutation RevokeEvaluationApiKey($id: ID!) {
    revokeEvaluationApiKey(id: $id)
  }
`;

/** Mirrors backend ``sequences.custom_evaluation_tests`` JSON definitions. */
export interface CustomEvalTestDefinition {
  id?: string;
  type: 'range' | 'contains_text' | 'json_contains' | 'llm_assert';
  path?: string;
  min?: number;
  max?: number;
  substring?: string;
  case_insensitive?: boolean;
  subset?: unknown;
  prompt?: string;
  expect?: {
    type: 'contains_text' | 'json_contains';
    substring?: string;
    case_insensitive?: boolean;
    subset?: unknown;
  };
}

export interface EvaluationAPIKeyItem {
  id: string;
  name: string;
  keyMasked: string;
  sampleRatePer100: number;
  isActive: boolean;
  createdAt: string;
}

export interface EvaluationTypeItem {
  id: string;
  evaluationApiKeyId: string;
  evaluationApiKeyName: string;
  evaluationKey: string;
  label: string;
  description: string;
  customTests: CustomEvalTestDefinition[];
  createdAt: string;
  updatedAt: string;
  totalRuns: number;
  completedRuns: number;
  failedRuns: number;
  pendingRuns: number;
  lastRunAt: string | null;
  customTestsPassRate: number | null;
}

export interface CreateEvaluationAPIKeyResult {
  id: string;
  name: string;
  key: string;
  secret: string;
  sampleRatePer100: number;
}

// CSV export for API Evaluations
export const EXPORT_API_EVALUATIONS_CSV_QUERY = gql`
  query ExportApiEvaluationsCsv($timeRange: String, $evaluationKey: String) {
    exportApiEvaluationsCsv(timeRange: $timeRange, evaluationKey: $evaluationKey)
  }
`;

// API Evaluation Requests (for API Evaluations page)
export const API_EVALUATION_REQUESTS_QUERY = gql`
  query ApiEvaluationRequests($limit: Int, $offset: Int, $timeRange: String, $evaluationKey: String) {
    totalApiEvaluationRequests(timeRange: $timeRange)
    apiEvaluationRequests(limit: $limit, offset: $offset, timeRange: $timeRange, evaluationKey: $evaluationKey) {
      id
      evaluationKey
      evaluationName
      status
      createdAt
      completedAt
      errorMessage
      isSafe
      overallHarmScore
      harmCategoryPasses
      harmCategoryFailures
      failedCategoryNames
      graniteRiskDetected
      graniteOverallScore
      llmSemanticAllPassed
      customTestsPerformed
      customTestsAllPassed
      inputData
      outputData
    }
  }
`;

export const API_EVALUATION_REQUEST_QUERY = gql`
  query ApiEvaluationRequest($id: ID!) {
    apiEvaluationRequest(id: $id) {
      id
      evaluationKey
      evaluationName
      status
      createdAt
      completedAt
      errorMessage
      isSafe
      overallHarmScore
      harmCategoryPasses
      harmCategoryFailures
      failedCategoryNames
      graniteRiskDetected
      graniteOverallScore
      llmSemanticAllPassed
      inputData
      outputData
      harmAnalysisPerformed
      passedCategoryNames
      ignoredCategoryNames
      harmAnalysisDetails
      graniteAnalysisPerformed
      graniteHarm
      graniteSocialBias
      graniteJailbreak
      graniteViolence
      graniteProfanity
      graniteSexualContent
      graniteUnethicalBehavior
      graniteCategoriesChecked
      graniteCategoriesPassed
      graniteCategoriesFailed
      graniteRawResults
      graniteModelUsed
      embeddingAnalysisPerformed
      embeddingAllPassed
      llmSemanticAnalysisPerformed
      llmGroundednessPassed
      llmGroundednessScore
      llmGroundednessExplanation
      llmHallucinationPassed
      llmHallucinationScore
      llmHallucinationExplanation
      llmTonePassed
      llmToneScore
      llmBiasScore
      customTestsPerformed
      customTestsAllPassed
      customTestsConfig
      customTestsResults
    }
  }
`;

export interface CustomEvalTestResult {
  id?: string | null;
  type?: string | null;
  passed: boolean;
  message: string;
  detail?: Record<string, unknown>;
}

export interface APIEvaluationRequestDetail extends APIEvaluationRequestItem {
  harmAnalysisPerformed: boolean;
  passedCategoryNames: string[];
  ignoredCategoryNames: string[];
  harmAnalysisDetails: string;
  graniteAnalysisPerformed: boolean;
  graniteHarm: boolean | null;
  graniteSocialBias: boolean | null;
  graniteJailbreak: boolean | null;
  graniteViolence: boolean | null;
  graniteProfanity: boolean | null;
  graniteSexualContent: boolean | null;
  graniteUnethicalBehavior: boolean | null;
  graniteCategoriesChecked: number;
  graniteCategoriesPassed: number;
  graniteCategoriesFailed: number;
  graniteRawResults: Record<string, { detected?: boolean; raw_response?: string }>;
  graniteModelUsed: string;
  embeddingAnalysisPerformed: boolean;
  embeddingAllPassed: boolean | null;
  llmSemanticAnalysisPerformed: boolean;
  llmGroundednessPassed: boolean | null;
  llmGroundednessScore: number | null;
  llmGroundednessExplanation: string;
  llmHallucinationPassed: boolean | null;
  llmHallucinationScore: number | null;
  llmHallucinationExplanation: string;
  llmTonePassed: boolean | null;
  llmToneScore: number | null;
  llmBiasScore: number | null;
  customTestsPerformed: boolean;
  customTestsAllPassed: boolean | null;
  customTestsConfig: CustomEvalTestDefinition[];
  customTestsResults: CustomEvalTestResult[];
}

export interface APIEvaluationRequestItem {
  id: string;
  evaluationKey: string;
  evaluationName: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
  errorMessage: string;
  isSafe: boolean;
  overallHarmScore: number | null;
  harmCategoryPasses: number;
  harmCategoryFailures: number;
  failedCategoryNames: string[];
  graniteRiskDetected: boolean;
  graniteOverallScore: number | null;
  llmSemanticAllPassed: boolean | null;
  customTestsPerformed?: boolean;
  customTestsAllPassed?: boolean | null;
  inputData: object;
  outputData: object;
}